import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import { DetailPageLayout } from "@dashboard/components/Layouts/Detail";
import { Savebar } from "@dashboard/components/Savebar";
import { type AtticErrorFragment, AtticLotSource } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { Box, Button, Input, Select, Text } from "@saleor/macaw-ui-next";
import { Trash2 } from "lucide-react";
import { useIntl } from "react-intl";

import { getAtticInventoryErrorMessage } from "../../errors";
import {
  type AtticIntakeFormData,
  type AtticIntakeLine,
  emptyIntakeLine,
  intakeTotals,
  isIntakeValid,
  lineErrorField,
  lineProblems,
  linesToSend,
  placeIntakeErrors,
} from "../../intake";
import { intakeMessages, sectionMessages, sourceMessages } from "../../messages";
import { atticLotListPath } from "../../urls";
import { formatCost } from "../../utils";
import { AtticInventoryNav } from "../AtticInventoryNav/AtticInventoryNav";

export interface AtticIntakePageProps {
  data: AtticIntakeFormData;
  errors: AtticErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onChange: (data: AtticIntakeFormData) => void;
  onSubmit: () => void;
}

export const AtticIntakePage = ({
  data,
  errors,
  disabled,
  saveButtonBarState,
  onChange,
  onSubmit,
}: AtticIntakePageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const change = (patch: Partial<AtticIntakeFormData>) => onChange({ ...data, ...patch });
  const changeLine = (key: number, patch: Partial<AtticIntakeLine>) =>
    change({ lines: data.lines.map(line => (line.key === key ? { ...line, ...patch } : line)) });
  const removeLine = (key: number) => {
    const lines = data.lines.filter(line => line.key !== key);

    change({ lines: lines.length ? lines : [emptyIntakeLine()] });
  };
  // A new line starts in the last line's location: a stack of cards usually
  // goes into one box.
  const addLine = () =>
    change({ lines: [...data.lines, emptyIntakeLine(data.lines.at(-1)?.location ?? "")] });
  const sourceOptions = Object.values(AtticLotSource).map(source => ({
    label: intl.formatMessage(sourceMessages[source]),
    value: source,
  }));
  const { byLine, form } = placeIntakeErrors(errors, data.lines);
  const sent = new Set(linesToSend(data.lines).map(line => line.key));
  const totals = intakeTotals(data.lines);

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <TopNav
        title={intl.formatMessage(sectionMessages.intake)}
        subtitle={
          <Text size={2} color="default2">
            {intl.formatMessage(intakeMessages.subtitle)}
          </Text>
        }
      />
      <DetailPageLayout.Content>
        <AtticInventoryNav current="intake" />
        <DashboardCard data-test-id="attic-intake-lot">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(intakeMessages.lotTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {form.map(error => (
              <Text
                key={`${error.field}-${error.message}`}
                as="p"
                size={3}
                color="critical1"
                marginBottom={3}
                data-test-id="attic-intake-form-error"
              >
                {getAtticInventoryErrorMessage(error, intl)}
              </Text>
            ))}
            <Box display="grid" gridTemplateColumns={{ mobile: 1, desktop: 2 }} gap={4}>
              <Input
                label={intl.formatMessage(intakeMessages.reference)}
                value={data.reference}
                disabled={disabled}
                onChange={event => change({ reference: event.target.value })}
                data-test-id="attic-intake-reference"
              />
              <Select
                label={intl.formatMessage(intakeMessages.source)}
                options={sourceOptions}
                value={
                  sourceOptions.find(option => option.value === data.source) ?? sourceOptions[0]
                }
                disabled={disabled}
                onChange={option => option && change({ source: option.value as AtticLotSource })}
                data-test-id="attic-intake-source"
              />
              <Input
                label={intl.formatMessage(intakeMessages.supplier)}
                value={data.supplier}
                disabled={disabled}
                onChange={event => change({ supplier: event.target.value })}
                data-test-id="attic-intake-supplier"
              />
              <Input
                label={intl.formatMessage(intakeMessages.currency)}
                value={data.currency}
                disabled={disabled}
                maxLength={3}
                onChange={event => change({ currency: event.target.value.toUpperCase() })}
                data-test-id="attic-intake-currency"
              />
            </Box>
            <Box marginTop={4}>
              <Input
                label={intl.formatMessage(intakeMessages.notes)}
                value={data.notes}
                disabled={disabled}
                onChange={event => change({ notes: event.target.value })}
                data-test-id="attic-intake-notes"
              />
            </Box>
          </DashboardCard.Content>
        </DashboardCard>
        <DashboardCard data-test-id="attic-intake-lines">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(intakeMessages.linesTitle)}
            </DashboardCard.Title>
            <DashboardCard.Toolbar>
              <Text size={3} color="default2" data-test-id="attic-intake-total">
                {intl.formatMessage(intakeMessages.total, {
                  units: totals.units,
                  cost: formatCost(totals.cost, data.currency || "USD", intl),
                })}
              </Text>
            </DashboardCard.Toolbar>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Box display="flex" flexDirection="column" gap={3}>
              {data.lines.map((line, index) => {
                // Problems are shown only on lines that will be sent.
                const problems = sent.has(line.key) ? lineProblems(line) : {};
                const refusals = byLine.get(line.key) ?? [];
                const refused = (field: string) =>
                  refusals.some(error => lineErrorField(error) === field);

                return (
                  <Box key={line.key} data-test-id="attic-intake-line">
                    <Box
                      display="grid"
                      __gridTemplateColumns="2fr 1.5fr 0.6fr 1fr auto"
                      gap={3}
                      alignItems="flex-end"
                    >
                      <Input
                        size="small"
                        label={intl.formatMessage(intakeMessages.sku)}
                        value={line.sku}
                        disabled={disabled}
                        onChange={event => changeLine(line.key, { sku: event.target.value })}
                        error={!!problems.sku || refused("sku")}
                        data-test-id="attic-intake-line-sku"
                      />
                      <Input
                        size="small"
                        label={intl.formatMessage(intakeMessages.location)}
                        value={line.location}
                        disabled={disabled}
                        onChange={event => changeLine(line.key, { location: event.target.value })}
                        error={!!problems.location || refused("location")}
                        data-test-id="attic-intake-line-location"
                      />
                      <Input
                        size="small"
                        type="number"
                        min={1}
                        label={intl.formatMessage(intakeMessages.quantity)}
                        value={line.quantity}
                        disabled={disabled}
                        onChange={event => changeLine(line.key, { quantity: event.target.value })}
                        error={!!problems.quantity || refused("quantity")}
                        data-test-id="attic-intake-line-quantity"
                      />
                      <Input
                        size="small"
                        label={intl.formatMessage(intakeMessages.unitCost)}
                        value={line.unitCost}
                        disabled={disabled}
                        onChange={event => changeLine(line.key, { unitCost: event.target.value })}
                        error={!!problems.unitCost || refused("unitCost")}
                        data-test-id="attic-intake-line-cost"
                      />
                      <Button
                        variant="tertiary"
                        size="small"
                        icon={<Trash2 size={16} />}
                        disabled={disabled}
                        onClick={() => removeLine(line.key)}
                        aria-label={intl.formatMessage(intakeMessages.removeLine, {
                          index: index + 1,
                        })}
                        data-test-id="attic-intake-line-remove"
                      />
                    </Box>
                    {refusals.map(error => (
                      <Text
                        key={`${error.field}-${error.message}`}
                        as="p"
                        size={2}
                        color="critical1"
                        marginTop={1}
                        data-test-id="attic-intake-line-error"
                      >
                        {intl.formatMessage(intakeMessages.lineError, {
                          index: index + 1,
                          message: getAtticInventoryErrorMessage(error, intl),
                        })}
                      </Text>
                    ))}
                  </Box>
                );
              })}
            </Box>
            <Box marginTop={4}>
              <Button
                variant="secondary"
                size="small"
                disabled={disabled}
                onClick={addLine}
                data-test-id="attic-intake-add-line"
              >
                {intl.formatMessage(intakeMessages.addLine)}
              </Button>
            </Box>
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.Content>
      <Savebar>
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(atticLotListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={onSubmit}
          disabled={disabled || !isIntakeValid(data)}
          data-test-id="attic-intake-save"
        >
          {intl.formatMessage(intakeMessages.save)}
        </Savebar.ConfirmButton>
      </Savebar>
    </DetailPageLayout>
  );
};

export default AtticIntakePage;
